import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Modal, Field, Badge, Spinner, EmptyState, Confirm } from "../components/ui";
import { SearchSelect, orgOptions } from "../components/SearchSelect";
import { RefSelect, GROUPES_PAYS, badgeDe, codeDe } from "../components/RefSelect";

export default function Leads() {
  const [leads, setLeads] = useState<any[] | null>(null);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [produits, setProduits] = useState<any[]>([]);
  const [refs, setRefs] = useState<any>(null);
  const [edit, setEdit] = useState<any>(null);
  const [del, setDel] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => {
    load();
    api.get("/organisations").then((r) => setOrgs(r.data));
    api.get("/produits").then((r) => setProduits(r.data));
    api.get("/referentiels").then((r) => setRefs(r.data));
  }, []);

  useEffect(() => {
    if (params.get("new")) {
      setEdit({ statut: "Nouveau" });
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  if (!leads || !refs) return <Spinner />;

  const listes = refs.listes || {};
  const statuts = (listes.statut_lead || []).filter((s: any) => s.actif);
  const sourceAutre = (listes.source_lead || []).find((s: any) => s.code === "autre")?.libelle || "Autre";
  const estDansLePipe = (l: any) => codeDe(listes.statut_lead, l.statut) === "dans_le_pipe";

  const save = async () => {
    setErr(null);
    if (edit.source === sourceAutre && !(edit.source_autre || "").trim()) {
      setErr("Précisez la source lorsque « Autre » est sélectionné.");
      return;
    }
    try {
      if (edit.id) await api.patch(`/leads/${edit.id}`, edit);
      else await api.post("/leads", edit);
      setEdit(null);
      load();
    } catch (e) {
      setErr(apiError(e));
    }
  };

  const convert = async (lead: any) => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/convert`);
      nav(`/devis/${data.devis_id}`);
    } catch (e) {
      alert(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/leads/${del.id}`);
      setDel(null);
      load();
    } catch (e) {
      alert(apiError(e));
    }
  };

  const sourceLabel = (l: any) => (l.source === sourceAutre && l.source_autre ? `${l.source} — ${l.source_autre}` : l.source || "—");

  return (
    <div data-testid="leads-page">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold text-green">Leads</h1>
        <button data-testid="lead-new-btn" className="nx-btn-primary" onClick={() => setEdit({ statut: "Nouveau" })}>
          Nouveau lead
        </button>
      </div>
      <div className="nx-card overflow-x-auto">
        {leads.length === 0 ? (
          <EmptyState message="Aucun lead" />
        ) : (
          <table className="nx-table">
            <thead>
              <tr><th>Réf.</th><th>Source</th><th>Client / prospect</th><th>Pays</th><th>Produit</th><th>Date</th><th>Statut</th><th>Partenaire</th><th>Report</th><th>Devis lié</th><th className="text-right">Actions</th></tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                  <td className="font-extrabold text-green" data-testid={`lead-reference-${l.id}`}>{l.reference || "—"}</td>
                  <td className="font-semibold" data-testid={`lead-source-${l.id}`}>{sourceLabel(l)}</td>
                  <td>{l.client_nom}</td>
                  <td>{l.pays}</td>
                  <td>{l.produit_nom}</td>
                  <td>{fmtDate(l.date)}</td>
                  <td>
                    {estDansLePipe(l) && l.devis_id ? (
                      <button data-testid={`lead-statut-${l.id}`} className="inline-flex" title={`Ouvrir le devis N° ${l.devis_numero}`} onClick={() => nav(`/devis/${l.devis_id}`)}>
                        <Badge className={`${badgeDe(listes.statut_lead, l.statut)} underline-offset-2 hover:underline`}>{l.statut} → N° {l.devis_numero}</Badge>
                      </button>
                    ) : (
                      <Badge testId={`lead-statut-${l.id}`} className={badgeDe(listes.statut_lead, l.statut)}>{l.statut}</Badge>
                    )}
                  </td>
                  <td>{l.partenaire_nom}</td>
                  <td>{fmtDate(l.reporte)}</td>
                  <td>{l.devis_numero ? (
                    <button className="font-bold text-green underline-offset-2 hover:underline" onClick={() => nav(`/devis/${l.devis_id}`)}>N° {l.devis_numero}</button>
                  ) : "—"}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      {!l.devis_id && (
                        <button data-testid={`lead-convert-btn-${l.id}`} className="nx-btn-lime !px-2 !py-1 text-xs" onClick={() => convert(l)}>
                          Convertir en devis
                        </button>
                      )}
                      <button data-testid={`lead-edit-btn-${l.id}`} className="nx-btn-ghost !px-2 !py-1 text-xs" onClick={() => setEdit({ ...l })}>
                        Éditer
                      </button>
                      <button data-testid={`lead-delete-btn-${l.id}`} className="nx-btn-danger !px-2 !py-1 text-xs" onClick={() => setDel(l)}>
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {edit && (
        <Modal title={edit.id ? `Lead ${edit.reference || ""} — Modifier` : "Nouveau lead"} onClose={() => setEdit(null)} testId="lead-modal">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Source">
              <RefSelect testId="lead-source-select" entries={listes.source_lead || []} value={edit.source} onChange={(v) => setEdit({ ...edit, source: v, source_autre: v === sourceAutre ? edit.source_autre : null })} />
            </Field>
            {edit.source === sourceAutre && (
              <Field label="Précision (obligatoire)">
                <input data-testid="lead-source-autre-input" className="nx-input" required value={edit.source_autre || ""} onChange={(e) => setEdit({ ...edit, source_autre: e.target.value })} />
              </Field>
            )}
            <Field label="Client / prospect">
              <input data-testid="lead-client-input" className="nx-input" value={edit.client_nom || ""} onChange={(e) => setEdit({ ...edit, client_nom: e.target.value })} />
            </Field>
            <Field label="Pays">
              <RefSelect testId="lead-pays-select" entries={listes.pays || []} groupes={GROUPES_PAYS} value={edit.pays} onChange={(v) => setEdit({ ...edit, pays: v })} />
            </Field>
            <Field label="Produit">
              <input data-testid="lead-produit-input" className="nx-input" list="produits-list" value={edit.produit_nom || ""} onChange={(e) => setEdit({ ...edit, produit_nom: e.target.value })} />
              <datalist id="produits-list">
                {produits.map((p) => <option key={p.id} value={p.nom} />)}
              </datalist>
            </Field>
            <Field label="Date">
              <input type="date" className="nx-input" value={(edit.date || "").slice(0, 10)} onChange={(e) => setEdit({ ...edit, date: e.target.value })} />
            </Field>
            <Field label="Statut">
              <select data-testid="lead-statut-select" className="nx-input" value={edit.statut} onChange={(e) => setEdit({ ...edit, statut: e.target.value })}>
                {edit.statut && !statuts.some((s: any) => s.libelle === edit.statut) && <option value={edit.statut}>{edit.statut} (valeur non conforme)</option>}
                {statuts.map((s: any) => <option key={s.id} value={s.libelle}>{s.libelle}</option>)}
              </select>
            </Field>
            <Field label="Partenaire / revendeur">
              <SearchSelect testId="lead-partenaire-select" options={orgOptions(orgs.filter((o) => o.type === "revendeur"))} value={edit.partenaire_id || null} onChange={(v) => setEdit({ ...edit, partenaire_id: v })} />
            </Field>
            <Field label="Report">
              <input type="date" className="nx-input" value={(edit.reporte || "").slice(0, 10)} onChange={(e) => setEdit({ ...edit, reporte: e.target.value || null })} />
            </Field>
            <Field label="Note" className="col-span-2">
              <textarea className="nx-input" rows={2} value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} />
            </Field>
          </div>
          {err && <p className="mt-2 text-sm text-danger" data-testid="lead-form-error">{err}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button className="nx-btn-ghost" onClick={() => setEdit(null)}>Annuler</button>
            <button data-testid="lead-save-btn" className="nx-btn-primary" onClick={save}>Enregistrer</button>
          </div>
        </Modal>
      )}

      {del && <Confirm message={`Supprimer le lead ${del.reference || ""} « ${del.client_nom} » ?`} onConfirm={remove} onCancel={() => setDel(null)} />}
    </div>
  );
}
